import { useEffect, useState } from "react";
import { Eye, ArrowLeft, Pencil, Lock } from "lucide-react";

interface GodModeBannerProps {
  workspaceName: string;
  writeEnabled: boolean;
  onToggleWrite: (enabled: boolean) => void;
  toggling: boolean;
  onExit: () => void;
  exiting: boolean;
}

export function GodModeBanner({
  workspaceName,
  writeEnabled,
  onToggleWrite,
  toggling,
  onExit,
  exiting,
}: GodModeBannerProps) {
  const mode = writeEnabled ? "Act" : "Observe";
  // Act mode makes live changes to another customer's workspace, so turning it
  // on takes a second, explicit click. The confirm is in the page rather than
  // window.confirm, whose native dialog freezes browser automation.
  const [confirming, setConfirming] = useState(false);
  // Any change of mode from outside (expiry, another tab) ends a pending ask.
  useEffect(() => setConfirming(false), [writeEnabled]);
  return (
    <div
      data-testid="god-mode-banner"
      className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 px-4 py-1.5 text-[13px] flex-shrink-0"
      style={{
        background: writeEnabled ? "var(--error)" : "var(--honesty)",
        color: writeEnabled ? "#ffffff" : "#ffffff",
      }}
    >
      {/* Both groups wrap: the banner sits in the center column, which an expanded sheet narrows to 320px, and a
          row that cannot wrap lets its fixed-width labels run under the buttons. */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0 max-w-full">
        {writeEnabled ? (
          <Pencil size={16} className="shrink-0" />
        ) : (
          <Eye size={16} className="shrink-0" />
        )}
        <span className="font-semibold shrink-0">Super Admin</span>
        <span style={{ opacity: 0.7 }} className="shrink-0" aria-hidden="true">
          &middot;
        </span>
        <span className="shrink-0">{mode}:</span>
        <span className="truncate min-w-0 max-w-full">
          <strong>{workspaceName}</strong>
        </span>
        <span
          className="px-1.5 py-0.5 rounded text-[11px] shrink-0 whitespace-nowrap"
          style={{ background: "rgba(0,0,0,0.12)" }}
        >
          {writeEnabled ? "editing records (live)" : "read-only"}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2 min-w-0 max-w-full">
        {confirming && !writeEnabled ? (
          <>
            <span role="alert" className="min-w-0">
              Make live, audited record changes to <strong>{workspaceName}</strong> for 15 minutes?
            </span>
            <button
              type="button"
              onClick={() => {
                setConfirming(false);
                onToggleWrite(true);
              }}
              disabled={toggling}
              className="flex items-center gap-1 px-2.5 py-1 rounded text-[12px] font-semibold border-none cursor-pointer disabled:opacity-50 whitespace-nowrap"
              style={{ background: "rgba(0,0,0,0.24)" }}
            >
              <Pencil size={12} />
              Confirm Act mode
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="flex items-center gap-1 px-2.5 py-1 rounded text-[12px] font-semibold border-none cursor-pointer whitespace-nowrap"
              style={{ background: "rgba(0,0,0,0.12)" }}
            >
              Cancel
            </button>
          </>
        ) : (
        <button
          type="button"
          onClick={() => (writeEnabled ? onToggleWrite(false) : setConfirming(true))}
          disabled={toggling}
          aria-pressed={writeEnabled}
          title={
            writeEnabled
              ? "Return to read-only Observe mode"
              : "Enable live record editing in this workspace (asks you to confirm; auto-expires in 15 min). Posting messages and approvals stay blocked."
          }
          className="flex items-center gap-1 px-2.5 py-1 rounded text-[12px] font-semibold border-none cursor-pointer disabled:opacity-50 whitespace-nowrap"
          style={{ background: "rgba(0,0,0,0.12)" }}
        >
          {writeEnabled ? <Lock size={12} /> : <Pencil size={12} />}
          {writeEnabled ? "Switch to Observe" : "Enable Act mode"}
        </button>
        )}
        <button
          type="button"
          onClick={onExit}
          disabled={exiting}
          className="flex items-center gap-1 px-2.5 py-1 rounded text-[12px] font-semibold border-none cursor-pointer disabled:opacity-50 whitespace-nowrap"
          style={{ background: "rgba(0,0,0,0.12)" }}
        >
          <ArrowLeft size={12} />
          Exit to Admin
        </button>
      </div>
    </div>
  );
}
